/** Звуковые эффекты через Web Audio вместо HTMLAudioElement.
 *
 *  Зачем: на HTMLAudioElement каждое `volume = …`, `playbackRate = …` и
 *  особенно `currentTime = 0` — это работа с медиа-конвейером. В Safari смена
 *  playbackRate переинициализирует ресемплер, а seek сбрасывает буферизацию,
 *  и делается это СИНХРОННО в основном потоке. Игра писала эти свойства
 *  каждый кадр каждому футстеп-лупу (локальный + все NPC ≈ 600 записей/с) и
 *  делала seek на каждый выстрел — отсюда рывки в Safari именно на ходу и при
 *  стрельбе, которых нет ни в Chrome (там записи дешёвые), ни на iOS (там звук
 *  без user-gesture вовсе не стартует).
 *
 *  В Web Audio громкость и скорость — это AudioParam: запись в него дешёвая и
 *  не трогает декодер. Одноразовые звуки — AudioBufferSourceNode, они и
 *  задуманы одноразовыми (создать/выбросить дешевле, чем seek по элементу).
 *
 *  Музыка сюда НЕ переносится: длинные потоки корректнее стримить <audio>,
 *  и там нет ни поштучных seek'ов, ни правки playbackRate.
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let unlocked = false;
const buffers = new Map<string, AudioBuffer>();
const pending = new Map<string, Promise<AudioBuffer | null>>();

/** Создаём контекст лениво: до жеста пользователя он всё равно suspended. */
function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Вызывать из обработчика реального жеста (pointerdown/keydown). */
export function unlockSfx(): void {
  const c = ensureCtx();
  if (!c) return;
  if (c.state === "suspended") void c.resume().catch(() => { /* */ });
  unlocked = c.state === "running";
}

export function sfxReady(): boolean {
  return ctx !== null && ctx.state === "running";
}

/** Декодировать и закэшировать буфер. Повторные вызовы дешёвые. */
export async function loadSfx(url: string): Promise<AudioBuffer | null> {
  const cached = buffers.get(url);
  if (cached) return cached;
  const inflight = pending.get(url);
  if (inflight) return inflight;
  const c = ensureCtx();
  if (!c) return null;
  const p = (async () => {
    try {
      const resp = await fetch(url);
      const raw = await resp.arrayBuffer();
      const buf = await c.decodeAudioData(raw);
      buffers.set(url, buf);
      return buf;
    } catch {
      return null;
    } finally {
      pending.delete(url);
    }
  })();
  pending.set(url, p);
  return p;
}

/** Одноразовый звук (выстрел, пустой магазин). Fire-and-forget. */
export function playOneShot(url: string, volume: number, rate = 1): void {
  const c = ensureCtx();
  if (!c || !master || volume <= 0) return;
  const buf = buffers.get(url);
  if (!buf) { void loadSfx(url); return; } // первый вызов прогреет кэш
  if (c.state === "suspended") return;
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = c.createGain();
    g.gain.value = Math.min(1, Math.max(0, volume));
    src.connect(g).connect(master);
    src.start();
    src.onended = () => { try { src.disconnect(); g.disconnect(); } catch { /* */ } };
  } catch { /* */ }
}

/** Зацикленный источник с регулируемыми громкостью и скоростью (шаги).
 *  set() пишет в AudioParam — это дёшево и не трогает декодер. */
export class SfxLoop {
  private src: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  private starting = false;

  constructor(private readonly url: string) {}

  /** volume 0 останавливает петлю (источник нельзя перезапустить — создаём заново). */
  set(volume: number, rate: number): void {
    const c = ensureCtx();
    if (!c || !master) return;
    const v = Math.min(1, Math.max(0, volume));
    if (v <= 0.0005) { this.stop(); return; }
    if (c.state === "suspended") return;
    if (!this.src) {
      const buf = buffers.get(this.url);
      if (!buf) {
        if (!this.starting) { this.starting = true; void loadSfx(this.url).then(() => { this.starting = false; }); }
        return;
      }
      try {
        const src = c.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        const g = c.createGain();
        g.gain.value = v;
        src.playbackRate.value = rate;
        src.connect(g).connect(master);
        src.start();
        this.src = src;
        this.gain = g;
        return;
      } catch { return; }
    }
    if (this.gain) this.gain.gain.value = v;
    this.src.playbackRate.value = rate;
  }

  stop(): void {
    if (!this.src) return;
    try { this.src.stop(); } catch { /* */ }
    try { this.src.disconnect(); this.gain?.disconnect(); } catch { /* */ }
    this.src = null;
    this.gain = null;
  }
}

export function stopAllSfx(): void {
  if (!ctx || !master) return;
  try {
    master.disconnect();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
  } catch { /* */ }
}

void unlocked;
